// src/http.ts
var HttpClient = class {
  constructor(baseUrl, authToken) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.authToken = authToken;
  }
  updateToken(token) {
    this.authToken = token;
  }
  async get(path) {
    return this.request("GET", path);
  }
  async post(path, body) {
    return this.request("POST", path, body);
  }
  async delete(path) {
    return this.request("DELETE", path);
  }
  async uploadFile(path, file) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: {
        "X-Customer-Token": this.authToken
      },
      body: formData
    });
    return this.handleResponse(response);
  }
  async request(method, path, body) {
    const headers = {
      "X-Customer-Token": this.authToken
    };
    if (body !== void 0) {
      headers["Content-Type"] = "application/json";
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15e3);
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body !== void 0 ? JSON.stringify(body) : void 0,
      signal: controller.signal
    }).finally(() => clearTimeout(timer));
    return this.handleResponse(response);
  }
  async handleResponse(response) {
    const json = await response.json();
    if (!response.ok || json.success === false) {
      const error = {
        code: json.error?.code ?? "UNKNOWN_ERROR",
        message: json.error?.message ?? "Request failed",
        statusCode: response.status
      };
      throw error;
    }
    return json.data;
  }
};

// src/events.ts
var EventEmitter = class {
  constructor() {
    this.handlers = /* @__PURE__ */ new Map();
  }
  on(event, handler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, /* @__PURE__ */ new Set());
    }
    this.handlers.get(event).add(handler);
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }
  off(event, handler) {
    this.handlers.get(event)?.delete(handler);
  }
  emit(event, ...args) {
    this.handlers.get(event)?.forEach((h) => h(...args));
    this.handlers.get("*")?.forEach((h) => h(event, ...args));
  }
  removeAllListeners() {
    this.handlers.clear();
  }
};

// src/websocket.ts
var WebSocketManager = class {
  constructor(wsUrl, authToken, reconnect = true, maxRetries = 5) {
    this.ws = null;
    this.state = "disconnected";
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.subscribedChannels = /* @__PURE__ */ new Set();
    this.messageIdCounter = 0;
    this.events = new EventEmitter();
    /** Extra options passed to the WebSocket constructor (e.g. { headers } for React Native). */
    this.wsOptions = {};
    this.baseWsUrl = wsUrl;
    this.url = `${wsUrl}?token=${encodeURIComponent(authToken)}`;
    this.shouldReconnect = reconnect;
    this.maxReconnectAttempts = maxRetries;
  }
  setOptions(opts) {
    this.wsOptions = opts;
  }
  /** Set a callback that fetches a fresh single-use WS ticket before each connection. */
  setTicketFetcher(fetcher) {
    this.ticketFetcher = fetcher;
  }
  async connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }
    if (this.ticketFetcher) {
      const ticket = await this.ticketFetcher();
      this.url = `${this.baseWsUrl}?ticket=${encodeURIComponent(ticket)}`;
    }
    return new Promise((resolve, reject) => {
      let settled = false;
      let authenticated = false;
      const settle = (fn) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          fn();
        }
      };
      const timeout = setTimeout(() => {
        settle(() => {
          this.ws?.close();
          this.setState("disconnected");
          reject(new Error("WebSocket connection timed out"));
        });
      }, 15e3);
      this.setState("connecting");
      let ws;
      try {
        const isNonBrowserWs = typeof navigator === "undefined" || navigator.product === "ReactNative";
        ws = isNonBrowserWs && Object.keys(this.wsOptions).length ? new WebSocket(this.url, void 0, this.wsOptions) : new WebSocket(this.url);
        this.ws = ws;
      } catch (err) {
        settle(() => {
          this.setState("disconnected");
          reject(err);
        });
        return;
      }
      ws.onopen = () => {
        this.reconnectAttempts = 0;
      };
      ws.onmessage = (event) => {
        try {
          const raw = typeof event.data === "string" ? event.data : String(event.data);
          const data = JSON.parse(raw);
          this.handleMessage(data);
          if (data.type === "connected") {
            settle(() => {
              authenticated = true;
              this.setState("connected");
              for (const channel of this.subscribedChannels) {
                this.sendSubscribe(channel);
              }
              resolve();
            });
          }
        } catch {
        }
      };
      ws.onerror = () => {
        settle(() => {
          this.setState("disconnected");
          reject(new Error("WebSocket connection failed"));
        });
      };
      ws.onclose = () => {
        settle(() => {
          this.setState("disconnected");
          reject(new Error("WebSocket closed before authenticated"));
        });
        if (authenticated) {
          this.setState("disconnected");
          this.attemptReconnect();
        }
      };
    });
  }
  disconnect() {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setState("disconnected");
    this.events.removeAllListeners();
  }
  subscribe(channel) {
    this.subscribedChannels.add(channel);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendSubscribe(channel);
    }
  }
  unsubscribe(channel) {
    this.subscribedChannels.delete(channel);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({
        method: "unsubscribe",
        channels: [channel],
        id: `unsub-${++this.messageIdCounter}`
      });
    }
  }
  getState() {
    return this.state;
  }
  sendSubscribe(channel) {
    this.send({
      method: "subscribe",
      channels: [channel],
      id: `sub-${++this.messageIdCounter}`
    });
  }
  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
  handleMessage(msg) {
    if (msg.e && msg.r) {
      const eventType = msg.r.type;
      const channelParam = msg.p;
      this.events.emit(eventType, { ...msg.r, _channel: channelParam });
      return;
    }
    if (msg.type) {
      this.events.emit(msg.type, msg);
      return;
    }
    if (msg.status === "error") {
      this.events.emit("error", { code: msg.code, error: msg.error, channel: msg.channel });
    }
  }
  setState(state) {
    if (this.state !== state) {
      this.state = state;
      this.events.emit("connection_change", state);
    }
  }
  attemptReconnect() {
    if (!this.shouldReconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }
    this.setState("reconnecting");
    this.reconnectAttempts++;
    const delay = Math.min(1e3 * Math.pow(2, this.reconnectAttempts - 1), 3e4);
    this.reconnectTimer = setTimeout(() => {
      this.connect().catch(() => {
      });
    }, delay);
  }
};

// src/utils.ts
function formatMessageTime(timestamp) {
  const date = new Date(timestamp * 1e3);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}
function toChatPhase(status) {
  switch (status) {
    case "active":
    case "waiting":
      return "chat";
    case "resolved":
      return "resolved";
    case "closed":
      return "closed";
  }
}
function derivePhase(status, feedback) {
  if (feedback && feedback.status === "pending") {
    return "rating";
  }
  return toChatPhase(status);
}

// src/client.ts
function detectClientChannel() {
  try {
    const nav = typeof navigator !== "undefined" ? navigator : void 0;
    const ua = nav?.userAgent ?? "";
    if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) {
      return "mobile";
    }
  } catch {
  }
  return "web";
}
var HelpdeskClient = class {
  /**
   * Create an anonymous guest session. Returns a session token
   * that can be used as `authToken` when constructing a new HelpdeskClient.
   *
   * Call this BEFORE creating a client instance — the returned token
   * is passed to the constructor. Requires `useWsTicket: true` in config.
   *
   * `phone` is optional; when provided it is forwarded to the backend as
   * the national part without the trunk zero (e.g. TR `5346536990`).
   * `tckn` is optional in the same way — the 11-digit Turkish national ID.
   * Both are omitted from the request body when empty.
   */
  static async createGuestSession(apiUrl, name, email, phone, tckn) {
    const baseUrl = apiUrl.replace(/\/$/, "");
    const body = { name, email };
    if (phone) body.phone = phone;
    if (tckn) body.tckn = tckn;
    const response = await fetch(`${baseUrl}/api/v1/customer/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const json2 = await response.json().catch(() => ({}));
      throw {
        code: json2.error?.code ?? "SESSION_FAILED",
        message: json2.error?.message ?? `Session creation failed (${response.status})`,
        statusCode: response.status
      };
    }
    const json = await response.json();
    const data = json.data ?? json;
    return data.session_token;
  }
  constructor(config) {
    this.config = config;
    this.http = new HttpClient(config.apiUrl, config.authToken);
    this.ws = new WebSocketManager(
      config.wsUrl,
      config.authToken,
      config.reconnect ?? true,
      config.reconnectMaxRetries ?? 5
    );
    if (config.useWsTicket) {
      this.ws.setTicketFetcher(() => this.fetchWsTicket());
    }
    this.ws.setOptions({
      headers: {
        "User-Agent": "ParibuPass/Mobile",
        "Origin": config.apiUrl
      }
    });
  }
  /** Connect to the WebSocket server. Must be called before receiving real-time events. */
  async connect() {
    await this.ws.connect();
  }
  /** Fetch a single-use WebSocket ticket (valid for 30 seconds). */
  async fetchWsTicket() {
    const data = await this.http.post("/api/v1/customer/ws-ticket");
    return data.ticket;
  }
  /** Disconnect from WebSocket and clean up all listeners. */
  disconnect() {
    this.ws.disconnect();
  }
  /** Check if WebSocket is connected. */
  isConnected() {
    return this.ws.getState() === "connected";
  }
  /** Get the current WebSocket connection state. */
  getConnectionState() {
    return this.ws.getState();
  }
  // ── Chat Lifecycle ──
  /** Start a new chat (creates a ticket with initial message). */
  async startNewChat(subjectId, message, requesterName, requesterEmail) {
    const body = {
      subject: message.substring(0, 100),
      message_body: message,
      requester_name: requesterName ?? "",
      requester_email: requesterEmail ?? "",
      channel: detectClientChannel()
    };
    if (subjectId) {
      body.subject_id = subjectId;
    }
    const data = await this.http.post("/api/v1/customer/tickets", body);
    const chat = this.mapTicketToChat(data);
    this.ws.subscribe(`ticket:${data.id}`);
    // Truthy check rather than strict ===true so we accept whatever the
    // BE serializer produces (boolean true, "true", 1). False/missing all
    // collapse to false correctly.
    const redirected = Boolean(data.redirected);
    const result = { chat, redirected };
    if (data.redirect_reason) result.redirectReason = data.redirect_reason;
    if (redirected && Array.isArray(data.messages)) {
      result.messages = data.messages.map((m) => this.mapMessage(m, String(data.id)));
    }
    return result;
  }
  /** List all chats for the authenticated customer. */
  async listChats(page = 1, perPage = 20) {
    const data = await this.http.get(
      `/api/v1/customer/tickets?page=${page}&per_page=${perPage}`
    );
    const tickets = data?.tickets ?? [];
    return {
      chats: tickets.map((t) => this.mapTicketToChat(t)),
      total: data?.pagination?.total ?? 0,
      openTotal: data?.open_total ?? 0
    };
  }
  /** Get a single chat with full message history. */
  async getChat(chatId) {
    const data = await this.http.get(
      `/api/v1/customer/tickets/${chatId}?include_messages=true`
    );
    this.ws.subscribe(`ticket:${chatId}`);
    return this.mapTicketToChatDetail(data);
  }
  /** Send a message in a chat. */
  async sendMessage(chatId, text) {
    const data = await this.http.post(
      `/api/v1/customer/tickets/${chatId}/messages`,
      { body: text }
    );
    return this.mapMessage(data, chatId);
  }
  /** Close/end a chat. */
  async endChat(chatId) {
    await this.http.post(`/api/v1/customer/tickets/${chatId}/close`);
    this.ws.unsubscribe(`ticket:${chatId}`);
  }
  // ── Real-time ──
  /** Send a typing indicator. */
  sendTypingIndicator(chatId, isTyping) {
    this.http.post(`/api/v1/customer/tickets/${chatId}/typing`, {
      is_typing: isTyping
    }).catch(() => {
    });
  }
  /** Subscribe to real-time updates for a chat. */
  subscribeToChat(chatId) {
    this.ws.subscribe(`ticket:${chatId}`);
  }
  /** Unsubscribe from real-time updates for a chat. */
  unsubscribeFromChat(chatId) {
    this.ws.unsubscribe(`ticket:${chatId}`);
  }
  // ── Events ──
  /** Listen for any event. */
  on(event, handler) {
    return this.ws.events.on(event, handler);
  }
  /** Listen for new messages. */
  onMessage(handler) {
    return this.ws.events.on("new_message", (data) => {
      handler({
        id: String(data.message_id),
        chatId: String(data.ticket_id),
        body: data.body ?? "",
        authorName: data.author_name ?? this.getSupportName(data.author_type),
        authorType: this.mapAuthorType(data.author_type),
        authorRole: this.mapAuthorRole(data.author_type),
        attachments: [],
        timestamp: data.timestamp ?? Date.now() / 1e3
      });
    });
  }
  /** Listen for typing indicators. */
  onTyping(handler) {
    return this.ws.events.on("typing", (data) => {
      handler({
        chatId: String(data.ticket_id ?? data._channel),
        isTyping: data.is_typing ?? false,
        userName: data.user_name ?? ""
      });
    });
  }
  /** Listen for chat status changes. */
  onStatusChange(handler) {
    return this.ws.events.on("status", (data) => {
      handler({
        chatId: String(data.ticket_id ?? data._channel),
        status: this.mapStatus(data.status)
      });
    });
  }
  /** Listen for new attachments added to a message. */
  onAttachmentAdded(handler) {
    return this.ws.events.on("new_attachment", (data) => {
      if (!data?.attachment) return;
      handler({
        chatId: String(data.ticket_id ?? data._channel),
        messageId: String(data.message_id),
        attachment: this.mapAttachment(data.attachment),
        authorType: this.mapAuthorType(data.author_type),
        internalNote: !!data.internal_note,
        timestamp: data.timestamp ?? Date.now() / 1e3
      });
    });
  }
  /** Listen for feedback requests. */
  onFeedbackRequested(handler) {
    return this.ws.events.on("feedback_triggered", (data) => {
      handler({
        chatId: String(data.ticket_id ?? data._channel),
        ticketId: data.ticket_id
      });
    });
  }
  /** Listen for connection state changes. */
  onConnectionChange(handler) {
    return this.ws.events.on("connection_change", handler);
  }
  // ── Feedback ──
  /** Get feedback status for a chat. */
  async getFeedback(chatId) {
    return this.http.get(`/api/v1/customer/tickets/${chatId}/feedback`);
  }
  /** Submit feedback for a chat. */
  async submitFeedback(chatId, rating, choiceIds, comment) {
    await this.http.post(`/api/v1/customer/tickets/${chatId}/feedback/submit`, {
      rating,
      choice_ids: choiceIds,
      comment
    });
  }
  /**
   * Resolve the correct UI phase for a chat the customer is opening.
   *
   * Fires `getChat` (which auto-subscribes to the channel) and
   * `getFeedback` in parallel, then derives the phase. Returns
   * `'rating'` when a pending survey from a prior session is waiting
   * — the case the one-shot `feedback_triggered` event cannot cover
   * on resume. A `FEEDBACK_NOT_FOUND` error is treated as "no survey
   * yet"; any other error is rethrown.
   */
  async getInitialPhase(chatId) {
    const [chat, feedback] = await Promise.all([
      this.getChat(chatId),
      this.getFeedback(chatId).catch((err) => {
        if (err?.code === "FEEDBACK_NOT_FOUND") {
          return null;
        }
        throw err;
      })
    ]);
    return derivePhase(chat.status, feedback);
  }
  // ── Attachments ──
  /** Upload a file attachment to a message. */
  async uploadAttachment(messageId, file) {
    const data = await this.http.uploadFile(
      `/api/v1/customer/messages/${messageId}/attachments`,
      file
    );
    return this.mapAttachment(data);
  }
  /** Get a temporary view URL for an attachment. */
  async getAttachmentViewUrl(attachmentId) {
    const data = await this.http.get(
      `/api/v1/customer/attachments/${attachmentId}/view-url`
    );
    return data.url;
  }
  // ── Subjects ──
  /** Get available chat subjects/categories. */
  async getSubjects() {
    const data = await this.http.get(
      "/api/v1/customer/tickets/subjects"
    );
    return data ?? [];
  }
  // ── Support Knowledge Base ──
  /**
   * Fetch the public help-center category tree with each category's
   * published articles (body-less). Subcategories nest one level under
   * their parent.
   *
   * Pass `platform` to restrict the result to articles that actually have
   * a body variant for that platform (recommended: `'app'` on mobile,
   * `'web'` on web). Omit it to receive everything.
   *
   * Cached on the client side is recommended — call this once per session.
   */
  async getSupportCategories(platform) {
    const qs = platform ? `?platform=${encodeURIComponent(platform)}` : "";
    const data = await this.http.get(
      `/api/v1/customer/support/categories${qs}`
    );
    return data?.categories ?? [];
  }
  /**
   * Fetch a single support article by its numeric id, including the
   * Markdown body for one or every available platform variant.
   *
   * If `platform` is supplied, only that body variant is returned and the
   * request resolves to `null` when the article has no body for that
   * platform (404 from server). Without `platform`, all variants are
   * returned in `body`.
   *
   * Drafts, unlisted, and soft-deleted articles always resolve to `null`.
   */
  async getSupportArticle(id, platform) {
    const qs = platform ? `?platform=${encodeURIComponent(platform)}` : "";
    try {
      return await this.http.get(
        `/api/v1/customer/support/articles/${id}${qs}`
      );
    } catch (err) {
      const e = err;
      if (e?.statusCode === 404) return null;
      throw err;
    }
  }
  // ── Mapping Helpers (backend → SDK types) ──
  mapTicketToChat(t) {
    return {
      id: String(t.id),
      status: this.mapStatus(t.status),
      subject: t.subject,
      createdAt: new Date(t.created_at).getTime() / 1e3,
      lastMessageAt: new Date(t.last_message_at).getTime() / 1e3
    };
  }
  mapTicketToChatDetail(t) {
    return {
      ...this.mapTicketToChat(t),
      messages: (t.messages ?? []).filter((m) => !m.internal_note).map((m) => this.mapMessage(m, String(t.id))),
      feedbackRequested: !!t.feedback_requested,
      feedbackSubmitted: !!t.feedback_submitted
    };
  }
  mapAttachment(a) {
    let createdAt;
    if (a.created_at) {
      const ms = new Date(a.created_at).getTime();
      if (Number.isFinite(ms)) createdAt = ms / 1e3;
    }
    return {
      id: String(a.id),
      fileName: a.file_name,
      contentType: a.content_type,
      sizeBytes: a.size_bytes,
      downloadUrl: a.download_url ?? a.view_url,
      createdAt
    };
  }
  mapMessage(m, chatId) {
    return {
      id: String(m.id),
      chatId,
      body: m.body,
      authorName: this.getSupportName(m.author_type),
      authorType: this.mapAuthorType(m.author_type),
      authorRole: this.mapAuthorRole(m.author_type),
      attachments: (m.attachments ?? []).map((a) => this.mapAttachment(a)),
      timestamp: new Date(m.created_at).getTime() / 1e3
    };
  }
  mapAuthorRole(backendType) {
    switch (backendType) {
      case "bot":
        return "bot";
      case "user":
        return "user";
      case "customer":
        return "customer";
      default:
        return "system";
    }
  }
  /** Map backend author types to simplified SDK types. Bot + Agent = 'support'. */
  mapAuthorType(backendType) {
    switch (backendType) {
      case "user":
      case "bot":
        return "support";
      case "customer":
        return "customer";
      default:
        return "system";
    }
  }
  /** Get display name for support messages. */
  getSupportName(authorType) {
    if (authorType === "customer") return "";
    if (authorType === "system") return "System";
    return "Support";
  }
  mapStatus(backendStatus) {
    switch (backendStatus) {
      case "new":
      case "open":
      case "bot_handling":
        return "active";
      case "pending":
        return "waiting";
      case "resolved":
        return "resolved";
      case "closed":
        return "closed";
      default:
        return "active";
    }
  }
};
export {
  HelpdeskClient,
  derivePhase,
  formatMessageTime,
  toChatPhase
};
//# sourceMappingURL=index.mjs.map